import React, { useState, useEffect } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { loadingCard } from "../../../helpers/loading";

ChartJS.register(ArcElement, Tooltip, Legend);

interface RawDataItem {
  exit_at: string;
  exit_note_id: string;
  exit_type: string;
}

interface ExitTypeChartProps {
  data: any;
}

export const ShelterExitTypePieChart = ({ data }: ExitTypeChartProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [typeGroups, setTypeGroups] = useState<any[]>([]);

  useEffect(() => {
    if (data) {
      let exitData;
      const { exits, start, end } = data;
      if (start && end) {
        exitData = exits?.filter((item: any) => {
          const exitDate = new Date(item.exit_at);
          return exitDate >= new Date(start) && exitDate <= new Date(end);
        });
      } else {
        exitData = exits;
      }
      const tGroups = groupByExitType(exitData).sort((a, b) => {
        if (a.label === "NONE") return 1;
        if (b.label === "NONE") return -1;
        return a.label.localeCompare(b.label);
      });
      setTypeGroups(tGroups);
    }
    setIsLoading(false);
  }, [data]);

  function groupByExitType(data: RawDataItem[]) {
    const counts: Record<string, { name: string; count: number }> = {};
    data?.forEach((item) => {
      const key = item.exit_type;
      const name = item.exit_type ? item.exit_type.toUpperCase() : "NONE";
      if (!counts[key]) {
        counts[key] = { name, count: 0 };
      }
      counts[key].count += 1;
    });
    return Object.entries(counts).map(([id, { name, count }]) => ({ id, label: name, numberCases: count }));
  }

  const chartData = {
    labels: typeGroups.length > 0 ? typeGroups.map((item) => item.label) : ["No data available"],
    datasets: [
      {
        label: "# People",
        data: typeGroups.length > 0 ? typeGroups.map((item) => item.numberCases) : [0],
        backgroundColor: ["#ed2f2f", "#DA7E39", "#B2BEB5", "#5A7082", "#5D82A0", "#F4A259", "#B5B2A4", "#A0C1B8", "#E07A5F", "#3D5A80"],
        borderWidth: 1,
      },
    ],
  };

  const options = {
    plugins: {
      maintainAspectRatio: false,
      title: {
        display: true,
        text: "Exits By Type",
        color: "#5a7082",
        font: {
          size: 18,
          weight: 500,
        },
      },
      legend: {
        display: true,
        position: "top" as const,
        align: "center" as const,
        labels: {
          boxWidth: 25,
          font: {
            size: 14,
          },
          padding: 15,
        },
      },
    },
    layout: {
      padding: {
        top: 0,
        bottom: 0,
      },
    },
  };

  if (isLoading) {
    return loadingCard();
  }

  return (
    <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Pie data={chartData} options={options} style={{ maxHeight: "100%", maxWidth: "100%" }} />
    </div>
  );
};
